import { useEffect } from 'react';

const SITE = {
  name: 'Disco Duck Events',
  url: 'https://www.discoduckevents.com',
  defaultImage: '/og-share.jpg',
  twitterCard: 'summary_large_image',
};

function setOrCreateMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => {
    el.setAttribute(k, v);
  });
}

function setOrCreateLink(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('link');
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => {
    el.setAttribute(k, v);
  });
}

function setOrCreateJsonLd(id, json) {
  const selector = `script[data-seo-jsonld="${id}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.setAttribute('data-seo-jsonld', id);
    document.head.appendChild(el);
  }
  el.text = JSON.stringify(json);
}

function toAbsoluteUrl(maybePath) {
  if (!maybePath) return SITE.url;
  if (maybePath.startsWith('http://') || maybePath.startsWith('https://')) return maybePath;
  return `${SITE.url}${maybePath.startsWith('/') ? '' : '/'}${maybePath}`;
}

export function useSeo({
  title,
  description,
  canonicalPath,
  image,
  imageAlt,
  type = 'website',
  jsonLd,
  noIndex = false,
}) {
  useEffect(() => {
    const canonicalUrl = toAbsoluteUrl(canonicalPath || window.location.pathname);
    const ogImage = toAbsoluteUrl(image || SITE.defaultImage);

    if (title) document.title = title;

    setOrCreateMeta('meta[name="description"]', { name: 'description', content: description || '' });
    setOrCreateMeta('meta[name="robots"]', {
      name: 'robots',
      content: noIndex ? 'noindex,nofollow' : 'index,follow',
    });

    setOrCreateLink('link[rel="canonical"]', { rel: 'canonical', href: canonicalUrl });

    setOrCreateMeta('meta[property="og:type"]', { property: 'og:type', content: type });
    setOrCreateMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: SITE.name });
    setOrCreateMeta('meta[property="og:title"]', { property: 'og:title', content: title || SITE.name });
    setOrCreateMeta('meta[property="og:description"]', {
      property: 'og:description',
      content: description || '',
    });
    setOrCreateMeta('meta[property="og:url"]', { property: 'og:url', content: canonicalUrl });
    setOrCreateMeta('meta[property="og:image"]', { property: 'og:image', content: ogImage });
    if (imageAlt) {
      setOrCreateMeta('meta[property="og:image:alt"]', { property: 'og:image:alt', content: imageAlt });
    }

    setOrCreateMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: SITE.twitterCard });
    setOrCreateMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: title || SITE.name });
    setOrCreateMeta('meta[name="twitter:description"]', {
      name: 'twitter:description',
      content: description || '',
    });
    setOrCreateMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: ogImage });

    const payloads = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
    payloads.forEach((obj, idx) => setOrCreateJsonLd(`main-${idx}`, obj));
    // drop blocks left over from a previous route that had more of them
    document.head.querySelectorAll('script[data-seo-jsonld^="main-"]').forEach((el) => {
      const idx = Number(el.getAttribute('data-seo-jsonld').slice(5));
      if (idx >= payloads.length) el.remove();
    });
  }, [title, description, canonicalPath, image, imageAlt, type, jsonLd, noIndex]);
}

